// @ts-ignore
import toastificationContent from "@/layouts/components/toastification/toastificationContent";
// @ts-ignore
import i18n from "@/libs/i18n";
import lodash from "lodash";

import ContactInterface from "@/interfaces/contact.interface";
import axios from "@/libs/axios";
import { computed, ref, watch } from "@vue/composition-api";
import { BTable, BvModal } from "bootstrap-vue";
import { useToast } from "vue-toastification/composition";
import ContactListInterface from "@/interfaces/contactList.interface";

const contactsList = ref<ContactListInterface>();

export default function useContactsLists(bvModal?: BvModal, sortable?: boolean) {
    const toast = useToast();
    const refTable = ref<BTable>();
    const tableColumns = [
        { key: "name", label: i18n.t("contacts.lists.table-column-name"), sortable },
        { key: "description", label: i18n.t("contacts.lists.table-column-description"), sortable },
        { key: "contacts", label: i18n.t("contacts.lists.table-column-total-contacts"), sortable: false },
        { key: "actions", label: i18n.t("contacts.lists.table-column-actions") },
    ];
    const perPage = ref(10);
    const total = ref(0);
    const currentPage = ref(1);
    const perPageOptions = [10, 25, 50, 100];
    const searchQuery = ref("");
    const sortBy = ref("name");
    const contactFilter = ref<(contact: ContactInterface, index: number, allContacts: ContactInterface[]) => boolean>();
    const isSortDirDesc = ref(false);
    const isLoading = ref(true);

    const dataMeta = computed(() => {
        const localItemsCount = refTable.value ? refTable.value.localItems.length : 0;

        return {
            from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
            to: perPage.value * (currentPage.value - 1) + localItemsCount,
            of: total.value,
        };
    });

    const refetchData = () => {
        refTable.value?.refresh();
    };

    watch([currentPage, perPage], () => {
        refetchData();
    });

    const queryTimeout = ref<NodeJS.Timeout>();
    watch(searchQuery, (query) => {
        if (query.length === 1) {
            return;
        }

        if (queryTimeout.value) {
            clearTimeout(queryTimeout.value);
        }

        queryTimeout.value = setTimeout(() => {
            refetchData();
        }, 500);
    });

    const fetchContactsLists = () => {
        isLoading.value = true;

        return axios
            .get("/contacts-lists", {
                params: {
                    fields: "name,description",
                    s: {
                        $or: [{ name: { $cont: searchQuery.value } }, { description: { $cont: searchQuery.value } }],
                    },
                    limit: perPage.value,
                    page: currentPage.value,
                    sort: sortBy.value ? `${sortBy.value},${isSortDirDesc.value === true ? "DESC" : "ASC"}` : undefined,
                },
            })
            .then((response) => {
                total.value = response.data.total;

                if (contactFilter.value) {
                    response.data.data.forEach((contactList: ContactListInterface) => {
                        contactList.contacts = contactList.contacts.filter(contactFilter.value!);
                    });
                }

                return response.data.data;
            })
            .finally(() => (isLoading.value = false));
    };

    const getAllContactsLists = async (withoutContacts = false) => {
        isLoading.value = true;

        const response = await axios.get(`/contacts-lists${withoutContacts ? "/without-contacts" : ""}`, {});
        total.value = response.data.total;

        if (contactFilter.value) {
            response.data.data.forEach((contactList: ContactListInterface) => {
                contactList.contacts = contactList.contacts.filter(contactFilter.value!);
            });
        }
        isLoading.value = false;
        return response.data;
    };

    // @todo we gota big problem of names there why two methodsd got the same name ?
    const fetchContactsList = async (id: number) => {
        if (isNaN(id)) {
            return;
        }
        const contactList = await axios
            .get(`contacts-lists/${id}`, {
                params: {
                    join: ["contacts", "contacts.labels"],
                },
            })
            .then((response) => response.data);

        const [receiveEngageMessagesList, ignoreEngageMessagesList] = lodash.partition(
            contactList.contacts,
            (contact) => contact.allowSystemMessages
        );
        contactsList.value = {
            ...contactList,
            contactsOptedIn: receiveEngageMessagesList,
            contactsOptedOut: ignoreEngageMessagesList,
        };
    };

    const deleteOne = ({ id, name }: { id: number; name: string }, callback: (data: unknown) => unknown) => {
        bvModal
            ?.msgBoxConfirm(i18n.t("confirm.Are you sure?"), {
                title: i18n.t("confirm.Are you sure?"),
                cancelTitle: i18n.t("confirm.Cancel"),
                cancelVariant: "light",
                okTitle: i18n.t("confirm.Yes, delete"),
                hideHeaderClose: false,
                headerCloseVariant: "light",
                centered: true,
            })
            .then((result) => {
                if (result) {
                    axios
                        .delete(`/contacts-lists/${id}`)
                        .then((response) => {
                            toast({
                                component: toastificationContent,
                                props: {
                                    title: i18n.t("toast.delete.title"),
                                    text: i18n.t("toast.delete.success", {
                                        entity: name,
                                    }),
                                    icon: "check",
                                    variant: "success",
                                },
                            });

                            callback(response.data);
                        })
                        .catch(() => {
                            toast({
                                component: toastificationContent,
                                props: {
                                    title: i18n.t("toast.delete.title"),
                                    text: i18n.t("toast.delete.error", {
                                        entity: name,
                                    }),
                                    icon: "times",
                                    variant: "danger",
                                },
                            });
                        });
                }
            });
    };

    const deleteListNoDialog = async (id) => {
        const res = await axios.delete(`/contacts-lists/${id}`);
        return res.data;
    };

    const hiddenFields = [
        "___rowId___",
        "id",
        "customerId",
        "country",
        "isHighPriority",
        "allowSystemMessages",
        "createdAt",
        "deletedAt",
    ];

    const columnPrio: Record<string, number> = {
        name: 40,
        phone: 30,
        email: 20,
        default: 10,
        labels: 5,
    };

    const getColumnPrio = (column: string): number => columnPrio[column] ?? columnPrio.default;

    const columns = computed(() => {
        if (!contactsList.value || !contactsList.value.contacts.length) {
            return ["name", "phone", "email"];
        }

        const columns: string[] = [];

        contactsList.value.contacts.forEach((contacts) => {
            Object.keys(contacts).forEach((column: any) => {
                if (hiddenFields.includes(column)) {
                    return;
                }

                // Typescript does not understand that Object.keys(contacts) is a keyOf column so I had to work around with the types
                if (contacts[column as keyof ContactInterface] && !columns.includes(column)) {
                    columns.push(column);
                }
            });
        });

        return columns.sort((columnA, columnB) => getColumnPrio(columnB) - getColumnPrio(columnA));
    });

    const createContactList = async (contactArray) => {
        const res = await axios.post("/contacts-lists", contactArray);
        return res.data;
    };

    return {
        fetchContactsLists,
        getAllContactsLists,
        fetchContactsList,
        deleteOne,
        refetchData,
        createContactList,
        deleteListNoDialog,
        contactFilter,
        columns,
        tableColumns,
        perPage,
        currentPage,
        total,
        dataMeta,
        perPageOptions,
        searchQuery,
        sortBy,
        isSortDirDesc,
        refTable,
        contactsList,
        isLoading,
    };
}
